import { useState, useEffect, useCallback } from "react";
import InfoIcon from "../../assets/icons/info";
import AppTooltip from "../AppTooltip";
import AppCharts from "../AppCharts";
import formatNumber from "../../services/formatNumber";
import { ChartMetrics } from "../../pages/home";
import "./DetailsCards.css";

type AnalyticDataType = {
  avg_cpm: number;
  ctr_rate: number;
  total_impressions: number;
  total_revenue: number;
};

type DateRangeMode = "Day" | "Week" | "Month" | "Custom";

interface Props {
  isAnalyticalView: boolean;
  analyticData?: AnalyticDataType | null;
  chartData?: ChartMetrics[] | null;
  dateRangeMode: DateRangeMode;
}

export default function DetailsCards({
  isAnalyticalView,
  analyticData,
  chartData,
  dateRangeMode,
}: Props) {
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
    isIcon?: boolean;
  } | null>(null);

  const handleMouseEnter = (
    e: React.MouseEvent,
    text: string,
    isIcon: boolean = false
  ) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 2,
      isIcon,
    });
  };

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    if (!isMobile) return;

    const handleScroll = () => {
      handleMouseLeave();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleMouseLeave]);

  const getChartDataByMetric = (metric: keyof ChartMetrics) => {
    if (!chartData) return [];
    return chartData.map((item) => ({
      name: item.display_name,
      pv: item[metric],
    }));
  };

  return (
    <>
      <div className="details-cards">
        <article className="details-cards-revenue">
          <div className="details-cards-title">
            <div className="card-title">
              <span>Revenue</span>
              <span
                className="card-title-icon"
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    "The total amount of money earned from all ads displayed.",
                    true
                  )
                }
                onMouseLeave={handleMouseLeave}
              >
                <InfoIcon />
              </span>
            </div>
            <strong>${analyticData?.total_revenue ?? "0"}</strong>
          </div>
          {isAnalyticalView && (
            <AppCharts
              height={140}
              color="#8884d8"
              data={getChartDataByMetric("total_revenue")}
              prefix={'$'}
            />
          )}
        </article>

        <article className="details-cards-impressions">
          <div className="details-cards-title">
            <div className="card-title">
              <span>Impressions</span>
              <span
                className="card-title-icon"
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    "The total number of times ads have been viewed by users.",
                    true
                  )
                }
                onMouseLeave={handleMouseLeave}
              >
                <InfoIcon />
              </span>
            </div>
            <strong>
              <span
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    `${(analyticData?.total_impressions ?? 0).toLocaleString()}`
                  )
                }
                onMouseLeave={handleMouseLeave}
              >
                {formatNumber(analyticData?.total_impressions ?? 0)}
              </span>
            </strong>
          </div>
          {isAnalyticalView && (
            <AppCharts
              height={140}
              color="#8884d8"
              data={getChartDataByMetric("total_impressions")}
            />
          )}
        </article>

        <article className="details-cards-fill-rate">
          <div className="details-cards-title">
            <div className="card-title">
              <span>Avg. CPM</span>
              <span
                className="card-title-icon"
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    "The average cost per thousand impressions, showing how much advertisers pay for every thousand views.",
                    true
                  )
                }
                onMouseLeave={handleMouseLeave}
              >
                <InfoIcon />
              </span>
            </div>
            <strong>${analyticData?.avg_cpm?.toFixed(2) ?? "0"}</strong>
          </div>
          {isAnalyticalView && (
            <AppCharts
              height={140}
              color="#8884d8"
              data={getChartDataByMetric("avg_cpm")}
              prefix={'$'}
            />
          )}
        </article>

        <article className="details-cards-ctr">
          <div className="details-cards-title">
            <div className="card-title">
              <span>CTR</span>
              <span
                className="card-title-icon"
                onMouseEnter={(e) =>
                  handleMouseEnter(
                    e,
                    "The click-through rate, which is the percentage of users who clicked on ads out of the total number of impressions.",
                    true
                  )
                }
                onMouseLeave={handleMouseLeave}
              >
                <InfoIcon />
              </span>
            </div>
            <strong>{analyticData?.ctr_rate?.toFixed(2) ?? "0"}%</strong>
          </div>
          {isAnalyticalView && (
            <AppCharts
              height={140}
              color="#8884d8"
              data={getChartDataByMetric("ctr_rate")}
              suffix={'%'}
            />
          )}
        </article>
      </div>

      {tooltip && (
        <AppTooltip
          text={tooltip.text}
          position={{ x: tooltip.x, y: tooltip.y }}
          isIcon={tooltip.isIcon}
        />
      )}
    </>
  );
}
